import * as THREE from "three";
import {
  Arc,
  Cuboid,
  Cylinder,
  Polygon,
  Polyline,
  Sweep,
  Vector3,
  booleanIntersection,
  booleanSubtraction,
  booleanUnion,
} from "opengeometry";

import type { Placement, PlanExportView, PlanVectorExportable } from "../types";
import { createOpenPlansId } from "../utils";
import { IShape } from "../shapes/base-type";
import { ElementType } from "./base-type";
import { buildControlSchema } from "../controls/control-descriptor";

type ColorValue = number | string;
type Point2 = [number, number];
type Point3 = [number, number, number];

interface ElementBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

interface BaseElementNode {
  id?: string;
  color?: ColorValue;
  placement?: Placement;
}

export interface RefNode extends BaseElementNode {
  ref: string;
}

export interface ProfilePolygonNode extends BaseElementNode {
  kind: "polygon";
  vertices: Point2[];
}

export interface ProfilePolylineNode extends BaseElementNode {
  kind: "polyline";
  points: Point2[];
}

export interface ProfileArcNode extends BaseElementNode {
  kind: "arc";
  center: Point2;
  radius: number;
  startAngle: number;
  endAngle: number;
  segments?: number;
}

export type ProfileNode = ProfilePolygonNode | ProfilePolylineNode | ProfileArcNode;
export type ProfileViewElement = ProfileNode | RefNode;

export interface Curve3DPolylineNode extends BaseElementNode {
  kind: "polyline3d";
  points: Point3[];
}

export type Curve3DNode = Curve3DPolylineNode;

export interface ModelCuboidNode extends BaseElementNode {
  kind: "cuboid";
  center: Point3;
  width: number;
  height: number;
  depth: number;
}

export interface ModelCylinderNode extends BaseElementNode {
  kind: "cylinder";
  center: Point3;
  radius: number;
  height: number;
  segments?: number;
  angle?: number;
}

export interface ModelSweepDirectNode extends BaseElementNode {
  kind: "sweep";
  path: Point3[];
  profile: Point3[];
  capStart?: boolean;
  capEnd?: boolean;
}

export interface ModelSweepRefNode extends BaseElementNode {
  kind: "sweep";
  pathRef: string;
  profileRef: string;
  capStart?: boolean;
  capEnd?: boolean;
}

export type ModelSweepNode = ModelSweepDirectNode | ModelSweepRefNode;
export type ModelNode = ModelCuboidNode | ModelCylinderNode | ModelSweepNode;
export type ModelViewElement = ModelNode | RefNode;

export interface Offset2DOpNode extends BaseElementNode {
  kind: "offset2d";
  sourceRef: string;
  distance: number;
  acuteThresholdDegrees?: number;
  bevel?: boolean;
  output: "polyline" | "polygon";
}

export interface Boolean3DOpNode extends BaseElementNode {
  kind: "boolean3d";
  operation: "subtract" | "union" | "intersect";
  baseRef: string;
  operandRefs: string[];
}

export type OperationNode = Offset2DOpNode | Boolean3DOpNode;
export type DefinitionNode = ProfileNode | Curve3DNode | ModelNode | OperationNode;
export type OpenElementDefinitions = Record<string, DefinitionNode>;

export interface AnchorDefinition {
  placement: Placement;
  metadata?: Record<string, string>;
}

export type OpenElementAnchors = Record<string, AnchorDefinition>;

export interface OpenElementDimensions {
  width: number;
  depth: number;
  height: number;
}

export interface OpenElementProfileView {
  elements: ProfileViewElement[];
}

export interface OpenElementModelView {
  elements: ModelViewElement[];
}

export interface OpenElementDefinition {
  version: 1;
  id: string;
  labelName: string;
  type: ElementType;
  dimensions: OpenElementDimensions;
  placement?: Placement;
  metadata?: Record<string, string>;
  anchors?: OpenElementAnchors;
  definitions?: OpenElementDefinitions;
  profileView?: OpenElementProfileView;
  modelView?: OpenElementModelView;
}

export interface OpenElementConfig extends OpenElementDefinition {
  ogid?: string;
}

// ---------------------------------------------------------------------------
// Control descriptor types – plain JSON for client-side UI consumption
// ---------------------------------------------------------------------------

export type ControlPropertyType = "number" | "color";

export interface PropertyDescriptor {
  key: string;
  type: ControlPropertyType;
  value: number | string;
  label?: string;
}

export interface NodeControlDescriptor {
  nodeId: string;
  nodeKind: string;
  section: "profileView" | "modelView" | "definitions";
  properties: PropertyDescriptor[];
}

export interface ControlSchema {
  nodes: NodeControlDescriptor[];
}

export interface OpenElementOptions extends Partial<Omit<OpenElementConfig, "version" | "dimensions">> {
  version?: 1;
  dimensions?: Partial<OpenElementDimensions>;
  source?: string | OpenElementDefinition;
}

export type OpenElementInput = string | OpenElementDefinition | OpenElementOptions;
export type OpenElementLoadOverrides = Partial<Omit<OpenElementOptions, "source">>;

interface RenderedVisualEntry {
  object: THREE.Object3D;
  color: number;
}

interface BooleanCapableObject extends THREE.Object3D {
  subtract?: (operands: BooleanCapableObject[], options?: Record<string, unknown>) => BooleanCapableObject;
  getBrepData?: () => unknown;
  getBrepSerialized?: () => string;
}

interface ResolvedProfileDescriptor {
  resultKind: "profile2d";
  node: ProfileNode;
  defaultColor: number;
  defaultId: string;
}

interface ResolvedCurveDescriptor {
  resultKind: "curve3d";
  node: Curve3DNode;
  defaultId: string;
}

interface ResolvedSolidDescriptor {
  resultKind: "solid3d";
  defaultColor: number;
  defaultId: string;
  createObject: () => BooleanCapableObject;
}

type ResolvedDefinitionDescriptor = ResolvedProfileDescriptor | ResolvedCurveDescriptor | ResolvedSolidDescriptor;

type ValidationResultKind = "profile2d" | "curve3d" | "solid3d";

interface ResolvedValidationDescriptor {
  resultKind: ValidationResultKind;
  profileKind?: ProfileNode["kind"];
}

const DEFAULT_PLACEMENT: Required<Placement> = {
  anchor: [0, 0, 0],
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

function degToRad(deg: number): number {
  return deg * (Math.PI / 180);
}

const DEFAULT_DIMENSIONS: OpenElementDimensions = {
  width: 1,
  depth: 1,
  height: 1,
};

const DEFAULT_PROFILE_COLOR = "#cfcfcf";
const DEFAULT_MODEL_COLOR = "#c4c4c4";

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeElementType(value: unknown, fallback: ElementType): ElementType {
  return typeof value === "string" && Object.values(ElementType).includes(value as ElementType)
    ? value as ElementType
    : fallback;
}

function normalizePlacement(placement?: Placement): Required<Placement> {
  const source = placement ?? DEFAULT_PLACEMENT;
  const anchor = Array.isArray(source.anchor) ? source.anchor : DEFAULT_PLACEMENT.anchor;
  const position = Array.isArray(source.position) ? source.position : DEFAULT_PLACEMENT.position;
  const rotation = Array.isArray(source.rotation) ? source.rotation : DEFAULT_PLACEMENT.rotation;
  const scale = Array.isArray(source.scale) ? source.scale : DEFAULT_PLACEMENT.scale;

  return {
    anchor: [
      toFiniteNumber(anchor[0], DEFAULT_PLACEMENT.anchor[0]),
      toFiniteNumber(anchor[1], DEFAULT_PLACEMENT.anchor[1]),
      toFiniteNumber(anchor[2], DEFAULT_PLACEMENT.anchor[2]),
    ],
    position: [
      toFiniteNumber(position[0], DEFAULT_PLACEMENT.position[0]),
      toFiniteNumber(position[1], DEFAULT_PLACEMENT.position[1]),
      toFiniteNumber(position[2], DEFAULT_PLACEMENT.position[2]),
    ],
    rotation: [
      toFiniteNumber(rotation[0], DEFAULT_PLACEMENT.rotation[0]),
      toFiniteNumber(rotation[1], DEFAULT_PLACEMENT.rotation[1]),
      toFiniteNumber(rotation[2], DEFAULT_PLACEMENT.rotation[2]),
    ],
    scale: [
      toFiniteNumber(scale[0], DEFAULT_PLACEMENT.scale[0]),
      toFiniteNumber(scale[1], DEFAULT_PLACEMENT.scale[1]),
      toFiniteNumber(scale[2], DEFAULT_PLACEMENT.scale[2]),
    ],
  };
}

function sanitizeMetadata(metadata?: Record<string, string>): Record<string, string> | undefined {
  if (!metadata) {
    return undefined;
  }

  const entries = Object.entries(metadata)
    .filter(([key]) => key.length > 0)
    .map(([key, value]) => [key, String(value)] as const);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeAnchorDefinition(value: unknown, context: string): AnchorDefinition {
  if (!isRecord(value)) {
    throw new Error(`${context} must be an object.`);
  }

  return {
    placement: normalizePlacement(value.placement as Placement | undefined),
    metadata: sanitizeMetadata(value.metadata as Record<string, string> | undefined),
  };
}

function normalizeAnchors(value: unknown): OpenElementAnchors | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value).map(([key, anchor]) => {
    if (key.length === 0) {
      throw new Error("anchors keys must be non-empty strings.");
    }

    return [key, normalizeAnchorDefinition(anchor, `anchors.${key}`)] as const;
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeDimensions(
  dimensions?: Partial<OpenElementDimensions>,
  base: OpenElementDimensions = DEFAULT_DIMENSIONS,
): OpenElementDimensions {
  return {
    width: Math.max(0.01, toFiniteNumber(dimensions?.width, base.width)),
    depth: Math.max(0.01, toFiniteNumber(dimensions?.depth, base.depth)),
    height: Math.max(0.01, toFiniteNumber(dimensions?.height, base.height)),
  };
}

function normalizePoint2Array(points: unknown, context: string): Point2[] {
  if (!Array.isArray(points) || points.length === 0) {
    throw new Error(`${context} must contain at least one point.`);
  }

  return points.map((point, index) => {
    if (!Array.isArray(point) || point.length < 2) {
      throw new Error(`${context}[${index}] must be a [x, z] coordinate pair.`);
    }

    return [
      toFiniteNumber(point[0], 0),
      toFiniteNumber(point[1], 0),
    ] satisfies Point2;
  });
}

function normalizePoint3Array(points: unknown, context: string): Point3[] {
  if (!Array.isArray(points) || points.length === 0) {
    throw new Error(`${context} must contain at least one point.`);
  }

  return points.map((point, index) => {
    if (!Array.isArray(point) || point.length < 3) {
      throw new Error(`${context}[${index}] must be a [x, y, z] coordinate triple.`);
    }

    return [
      toFiniteNumber(point[0], 0),
      toFiniteNumber(point[1], 0),
      toFiniteNumber(point[2], 0),
    ] satisfies Point3;
  });
}

function normalizeBaseNode(node: Record<string, unknown>) {
  return {
    id: typeof node.id === "string" && node.id.length > 0 ? node.id : undefined,
    color: typeof node.color === "string" || typeof node.color === "number" ? node.color : undefined,
    placement: normalizePlacement(node.placement as Placement | undefined),
  } satisfies BaseElementNode;
}

function normalizeRefNode(node: unknown, context: string): RefNode {
  if (!isRecord(node) || typeof node.ref !== "string" || node.ref.length === 0) {
    throw new Error(`${context} must be a ref object with a non-empty "ref" string.`);
  }
  if (Object.prototype.hasOwnProperty.call(node, "kind")) {
    throw new Error(`${context} cannot define both "ref" and "kind".`);
  }

  const normalized = normalizeBaseNode(node);
  return {
    ref: node.ref,
    id: normalized.id,
    color: normalized.color,
    placement: Object.prototype.hasOwnProperty.call(node, "placement") ? normalized.placement : undefined,
  };
}

function normalizeProfileNode(node: unknown, context: string): ProfileNode {
  if (!isRecord(node)) {
    throw new Error(`${context} must be an object.`);
  }

  const base = normalizeBaseNode(node);

  if (node.kind === "polygon") {
    return {
      kind: "polygon",
      ...base,
      vertices: normalizePoint2Array(node.vertices, `${context}.vertices`),
    };
  }

  if (node.kind === "polyline") {
    return {
      kind: "polyline",
      ...base,
      points: normalizePoint2Array(node.points, `${context}.points`),
    };
  }

  if (node.kind === "arc") {
    const center = normalizePoint2Array([node.center], `${context}.center`)[0];

    return {
      kind: "arc",
      ...base,
      center,
      radius: Math.max(0.01, toFiniteNumber(node.radius, 1)),
      startAngle: toFiniteNumber(node.startAngle, 0),
      endAngle: toFiniteNumber(node.endAngle, Math.PI * 2),
      segments: Math.max(4, Math.floor(toFiniteNumber(node.segments, 32))),
    };
  }

  throw new Error(
    `${context} has unsupported kind "${String(node.kind)}". Supported kinds: polygon, polyline, arc.`,
  );
}

function normalizeCurve3DNode(node: unknown, context: string): Curve3DNode {
  if (!isRecord(node)) {
    throw new Error(`${context} must be an object.`);
  }
  if (node.kind !== "polyline3d") {
    throw new Error(`${context} has unsupported kind "${String(node.kind)}". Supported kind: polyline3d.`);
  }

  return {
    kind: "polyline3d",
    ...normalizeBaseNode(node),
    points: normalizePoint3Array(node.points, `${context}.points`),
  };
}

function isSweepRefCandidate(node: Record<string, unknown>) {
  return typeof node.pathRef === "string" || typeof node.profileRef === "string";
}

function normalizeModelNode(node: unknown, context: string): ModelNode {
  if (!isRecord(node)) {
    throw new Error(`${context} must be an object.`);
  }

  const base = normalizeBaseNode(node);

  if (node.kind === "cuboid") {
    return {
      kind: "cuboid",
      ...base,
      center: normalizePoint3Array([node.center], `${context}.center`)[0],
      width: Math.max(0.01, toFiniteNumber(node.width, 1)),
      height: Math.max(0.01, toFiniteNumber(node.height, 1)),
      depth: Math.max(0.01, toFiniteNumber(node.depth, 1)),
    };
  }

  if (node.kind === "cylinder") {
    return {
      kind: "cylinder",
      ...base,
      center: normalizePoint3Array([node.center], `${context}.center`)[0],
      radius: Math.max(0.01, toFiniteNumber(node.radius, 1)),
      height: Math.max(0.01, toFiniteNumber(node.height, 1)),
      segments: Math.max(3, Math.floor(toFiniteNumber(node.segments, 24))),
      angle: toFiniteNumber(node.angle, Math.PI * 2),
    };
  }

  if (node.kind === "sweep") {
    if (isSweepRefCandidate(node)) {
      if (typeof node.pathRef !== "string" || node.pathRef.length === 0) {
        throw new Error(`${context}.pathRef must be a non-empty definition reference.`);
      }
      if (typeof node.profileRef !== "string" || node.profileRef.length === 0) {
        throw new Error(`${context}.profileRef must be a non-empty definition reference.`);
      }

      return {
        kind: "sweep",
        ...base,
        pathRef: node.pathRef,
        profileRef: node.profileRef,
        capStart: Boolean(node.capStart),
        capEnd: Boolean(node.capEnd),
      };
    }

    return {
      kind: "sweep",
      ...base,
      path: normalizePoint3Array(node.path, `${context}.path`),
      profile: normalizePoint3Array(node.profile, `${context}.profile`),
      capStart: Boolean(node.capStart),
      capEnd: Boolean(node.capEnd),
    };
  }

  throw new Error(
    `${context} has unsupported kind "${String(node.kind)}". Supported kinds: cuboid, cylinder, sweep.`,
  );
}

function normalizeOffsetOperationNode(node: unknown, context: string): Offset2DOpNode {
  if (!isRecord(node) || node.kind !== "offset2d") {
    throw new Error(`${context} must be an offset2d operation object.`);
  }
  if (typeof node.sourceRef !== "string" || node.sourceRef.length === 0) {
    throw new Error(`${context}.sourceRef must be a non-empty definition reference.`);
  }
  if (node.output !== "polyline" && node.output !== "polygon") {
    throw new Error(`${context}.output must be either "polyline" or "polygon".`);
  }

  return {
    kind: "offset2d",
    ...normalizeBaseNode(node),
    sourceRef: node.sourceRef,
    distance: toFiniteNumber(node.distance, 0),
    acuteThresholdDegrees: Object.prototype.hasOwnProperty.call(node, "acuteThresholdDegrees")
      ? toFiniteNumber(node.acuteThresholdDegrees, 0)
      : undefined,
    bevel: typeof node.bevel === "boolean" ? node.bevel : undefined,
    output: node.output,
  };
}

function normalizeBooleanOperationNode(node: unknown, context: string): Boolean3DOpNode {
  if (!isRecord(node) || node.kind !== "boolean3d") {
    throw new Error(`${context} must be a boolean3d operation object.`);
  }
  if (node.operation !== "subtract" && node.operation !== "union" && node.operation !== "intersect") {
    throw new Error(`${context}.operation must be "subtract", "union", or "intersect".`);
  }
  if (typeof node.baseRef !== "string" || node.baseRef.length === 0) {
    throw new Error(`${context}.baseRef must be a non-empty definition reference.`);
  }
  if (!Array.isArray(node.operandRefs) || node.operandRefs.length === 0) {
    throw new Error(`${context}.operandRefs must contain at least one definition reference.`);
  }

  const operandRefs = node.operandRefs.map((operandRef, index) => {
    if (typeof operandRef !== "string" || operandRef.length === 0) {
      throw new Error(`${context}.operandRefs[${index}] must be a non-empty definition reference.`);
    }
    return operandRef;
  });

  return {
    kind: "boolean3d",
    ...normalizeBaseNode(node),
    operation: node.operation,
    baseRef: node.baseRef,
    operandRefs,
  };
}

function normalizeDefinitionNode(node: unknown, context: string): DefinitionNode {
  if (!isRecord(node)) {
    throw new Error(`${context} must be an object.`);
  }

  if (node.kind === "polygon" || node.kind === "polyline" || node.kind === "arc") {
    return normalizeProfileNode(node, context);
  }
  if (node.kind === "polyline3d") {
    return normalizeCurve3DNode(node, context);
  }
  if (node.kind === "cuboid" || node.kind === "cylinder" || node.kind === "sweep") {
    return normalizeModelNode(node, context);
  }
  if (node.kind === "offset2d") {
    return normalizeOffsetOperationNode(node, context);
  }
  if (node.kind === "boolean3d") {
    return normalizeBooleanOperationNode(node, context);
  }

  throw new Error(
    `${context} has unsupported kind "${String(node.kind)}". Supported kinds: polygon, polyline, arc, polyline3d, cuboid, cylinder, sweep, offset2d, boolean3d.`,
  );
}

function normalizeProfileViewElement(node: unknown, index: number): ProfileViewElement {
  const context = `profileView.elements[${index}]`;
  return isRecord(node) && Object.prototype.hasOwnProperty.call(node, "ref")
    ? normalizeRefNode(node, context)
    : normalizeProfileNode(node, context);
}

function normalizeModelViewElement(node: unknown, index: number): ModelViewElement {
  const context = `modelView.elements[${index}]`;
  return isRecord(node) && Object.prototype.hasOwnProperty.call(node, "ref")
    ? normalizeRefNode(node, context)
    : normalizeModelNode(node, context);
}

function normalizeProfileView(view: unknown): OpenElementProfileView | undefined {
  if (!isRecord(view) || !Array.isArray(view.elements) || view.elements.length === 0) {
    return undefined;
  }

  return {
    elements: view.elements.map((node, index) => normalizeProfileViewElement(node, index)),
  };
}

function normalizeModelView(view: unknown): OpenElementModelView | undefined {
  if (!isRecord(view) || !Array.isArray(view.elements) || view.elements.length === 0) {
    return undefined;
  }

  return {
    elements: view.elements.map((node, index) => normalizeModelViewElement(node, index)),
  };
}

function normalizeDefinitions(definitions: unknown): OpenElementDefinitions | undefined {
  if (!isRecord(definitions)) {
    return undefined;
  }

  const entries = Object.entries(definitions).map(([key, value]) => {
    if (key.length === 0) {
      throw new Error("definitions keys must be non-empty strings.");
    }

    return [key, normalizeDefinitionNode(value, `definitions.${key}`)] as const;
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function parseDefinitionBuffer(source: string): OpenElementDefinition {
  return parseOpenElementDefinition(source);
}

function assignAutoIds(definition: OpenElementDefinition): void {
  let counter = 0;

  if (definition.profileView?.elements) {
    for (const element of definition.profileView.elements) {
      if (!("ref" in element) && !element.id) {
        element.id = `${element.kind}-${counter++}`;
      }
    }
  }

  if (definition.modelView?.elements) {
    for (const element of definition.modelView.elements) {
      if (!("ref" in element) && !element.id) {
        element.id = `${element.kind}-${counter++}`;
      }
    }
  }

  if (definition.definitions) {
    for (const [key, node] of Object.entries(definition.definitions)) {
      if (!node.id) {
        node.id = key;
      }
    }
  }
}

function normalizeDefinition(input: unknown): OpenElementDefinition {
  if (!isRecord(input)) {
    throw new Error("Generic element definition must be an object.");
  }

  const result: OpenElementDefinition = {
    version: 1,
    id: typeof input.id === "string" && input.id.length > 0
      ? input.id
      : createOpenPlansId("op-asset"),
    labelName: typeof input.labelName === "string" && input.labelName.length > 0
      ? input.labelName
      : "Generic Element",
    type: normalizeElementType(input.type, ElementType.FURNITURE),
    dimensions: normalizeDimensions(input.dimensions as Partial<OpenElementDimensions> | undefined),
    placement: normalizePlacement(input.placement as Placement | undefined),
    metadata: sanitizeMetadata(input.metadata as Record<string, string> | undefined),
    anchors: normalizeAnchors(input.anchors),
    definitions: normalizeDefinitions(input.definitions),
    profileView: normalizeProfileView(input.profileView),
    modelView: normalizeModelView(input.modelView),
  };

  assignAutoIds(result);
  return result;
}

function isDefinitionCandidate(value: OpenElementInput): value is OpenElementDefinition {
  return isRecord(value)
    && value.version === 1
    && typeof value.id === "string"
    && typeof value.labelName === "string"
    && isRecord(value.dimensions);
}

function applyConfigOverrides(base: OpenElementConfig, overrides: OpenElementOptions): OpenElementConfig {
  const next = cloneValue(base);

  if (Object.prototype.hasOwnProperty.call(overrides, "id") && typeof overrides.id === "string" && overrides.id.length > 0) {
    next.id = overrides.id;
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "ogid")) {
    next.ogid = overrides.ogid;
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "labelName") && typeof overrides.labelName === "string") {
    next.labelName = overrides.labelName;
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "type")) {
    next.type = normalizeElementType(overrides.type, next.type);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "dimensions")) {
    next.dimensions = normalizeDimensions(overrides.dimensions, next.dimensions);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "placement")) {
    next.placement = normalizePlacement(overrides.placement);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "metadata")) {
    next.metadata = sanitizeMetadata(overrides.metadata);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "anchors")) {
    next.anchors = overrides.anchors === undefined
      ? undefined
      : normalizeAnchors(overrides.anchors);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "definitions")) {
    next.definitions = overrides.definitions === undefined
      ? undefined
      : normalizeDefinitions(overrides.definitions);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "profileView")) {
    next.profileView = overrides.profileView === undefined
      ? undefined
      : normalizeProfileView(overrides.profileView);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, "modelView")) {
    next.modelView = overrides.modelView === undefined
      ? undefined
      : normalizeModelView(overrides.modelView);
  }

  return next;
}

function resolveOpenElementConfig(input?: OpenElementInput, current?: OpenElementConfig): OpenElementConfig {
  const baseConfig = current
    ? cloneValue(current)
    : {
        version: 1,
        id: createOpenPlansId("op-asset"),
        labelName: "Generic Element",
        type: ElementType.FURNITURE,
        dimensions: cloneValue(DEFAULT_DIMENSIONS),
        placement: cloneValue(DEFAULT_PLACEMENT),
        metadata: undefined,
        anchors: undefined,
        definitions: undefined,
        profileView: undefined,
        modelView: undefined,
      } satisfies OpenElementConfig;

  if (input === undefined) {
    return baseConfig;
  }

  if (typeof input === "string") {
    return {
      ogid: current?.ogid,
      ...parseDefinitionBuffer(input),
    };
  }

  if (isRecord(input) && Object.prototype.hasOwnProperty.call(input, "source") && input.source !== undefined) {
    const sourceConfig = typeof input.source === "string"
      ? parseDefinitionBuffer(input.source)
      : normalizeDefinition(input.source);

    return applyConfigOverrides(
      {
        ogid: current?.ogid,
        ...sourceConfig,
      },
      input,
    );
  }

  if (isDefinitionCandidate(input)) {
    return {
      ogid: current?.ogid,
      ...normalizeDefinition(input),
    };
  }

  return applyConfigOverrides(baseConfig, input);
}

function validateProfileNodeColor(node: ProfileNode, context: string) {
  if (node.color !== undefined) {
    toColorNumber(node.color, DEFAULT_PROFILE_COLOR);
  }

  if (node.kind === "polygon") {
    node.vertices.forEach((vertex, index) => {
      if (!Array.isArray(vertex) || vertex.length < 2) {
        throw new Error(`${context}.vertices[${index}] must be a [x, z] coordinate pair.`);
      }
    });
    return;
  }

  if (node.kind === "polyline") {
    node.points.forEach((point, index) => {
      if (!Array.isArray(point) || point.length < 2) {
        throw new Error(`${context}.points[${index}] must be a [x, z] coordinate pair.`);
      }
    });
  }
}

function validateModelNodeColor(node: ModelNode, context: string) {
  if (node.color !== undefined) {
    toColorNumber(node.color, DEFAULT_MODEL_COLOR);
  }

  if (node.kind === "cuboid") {
    node.center.forEach((value, index) => {
      if (!Number.isFinite(value)) {
        throw new Error(`${context}.center[${index}] must be a finite number.`);
      }
    });
    return;
  }

  if (node.kind === "cylinder") {
    node.center.forEach((value, index) => {
      if (!Number.isFinite(value)) {
        throw new Error(`${context}.center[${index}] must be a finite number.`);
      }
    });
    return;
  }

  if (hasInlineSweepGeometry(node)) {
    node.path.forEach((point, index) => {
      point.forEach((value, axis) => {
        if (!Number.isFinite(value)) {
          throw new Error(`${context}.path[${index}][${axis}] must be a finite number.`);
        }
      });
    });

    node.profile.forEach((point, index) => {
      point.forEach((value, axis) => {
        if (!Number.isFinite(value)) {
          throw new Error(`${context}.profile[${index}][${axis}] must be a finite number.`);
        }
      });
    });
  }
}

class DefinitionValidationResolver {
  private readonly cache = new Map<string, ResolvedValidationDescriptor>();
  private readonly stack: string[] = [];

  constructor(private readonly definitions?: OpenElementDefinitions) {}

  validateDefinition(ref: string, context: string): ResolvedValidationDescriptor {
    return this.resolveDefinition(ref, context);
  }

  resolveProfile(ref: string, context: string): ResolvedValidationDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "profile2d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of profile2d.`);
    }
    return resolved;
  }

  resolveCurve(ref: string, context: string): ResolvedValidationDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "curve3d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of curve3d.`);
    }
    return resolved;
  }

  resolveSolid(ref: string, context: string): ResolvedValidationDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "solid3d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of solid3d.`);
    }
    return resolved;
  }

  private resolveDefinition(ref: string, context: string): ResolvedValidationDescriptor {
    if (!this.definitions || !Object.prototype.hasOwnProperty.call(this.definitions, ref)) {
      throw new Error(`${context} references unknown definition "${ref}".`);
    }

    const cached = this.cache.get(ref);
    if (cached) {
      return cached;
    }

    if (this.stack.includes(ref)) {
      throw new Error(`Cyclic generic element definition detected: ${[...this.stack, ref].join(" -> ")}.`);
    }

    this.stack.push(ref);
    try {
      const descriptor = this.buildDescriptor(ref, this.definitions[ref]);
      this.cache.set(ref, descriptor);
      return descriptor;
    } finally {
      this.stack.pop();
    }
  }

  private buildDescriptor(ref: string, node: DefinitionNode): ResolvedValidationDescriptor {
    if (isProfileNode(node)) {
      validateProfileNodeColor(node, `definitions.${ref}`);
      return {
        resultKind: "profile2d",
        profileKind: node.kind,
      };
    }

    if (isCurve3DNode(node)) {
      node.points.forEach((point, index) => {
        point.forEach((value, axis) => {
          if (!Number.isFinite(value)) {
            throw new Error(`definitions.${ref}.points[${index}][${axis}] must be a finite number.`);
          }
        });
      });

      return {
        resultKind: "curve3d",
      };
    }

    if (isModelNode(node)) {
      validateModelNodeColor(node, `definitions.${ref}`);

      if (node.kind === "sweep" && !hasInlineSweepGeometry(node)) {
        this.resolveCurve(node.pathRef, `definitions.${ref}.pathRef`);
        this.resolveCurve(node.profileRef, `definitions.${ref}.profileRef`);
      }

      return {
        resultKind: "solid3d",
      };
    }

    if (node.kind === "offset2d") {
      const source = this.resolveProfile(node.sourceRef, `definitions.${ref}.sourceRef`);
      if (source.profileKind !== "polyline") {
        throw new Error(`definitions.${ref}.sourceRef must resolve to a polyline for offset2d.`);
      }

      if (node.color !== undefined) {
        toColorNumber(node.color, DEFAULT_PROFILE_COLOR);
      }

      return {
        resultKind: "profile2d",
        profileKind: node.output,
      };
    }

    this.resolveSolid(node.baseRef, `definitions.${ref}.baseRef`);
    node.operandRefs.forEach((operandRef, index) => {
      this.resolveSolid(operandRef, `definitions.${ref}.operandRefs[${index}]`);
    });

    if (node.color !== undefined) {
      toColorNumber(node.color, DEFAULT_MODEL_COLOR);
    }

    return {
      resultKind: "solid3d",
    };
  }
}

function validateProfileViewElement(
  element: ProfileViewElement,
  index: number,
  resolver: DefinitionValidationResolver,
) {
  if ("ref" in element) {
    if (element.color !== undefined) {
      toColorNumber(element.color, DEFAULT_PROFILE_COLOR);
    }
    resolver.resolveProfile(element.ref, `profileView.elements[${index}].ref`);
    return;
  }

  validateProfileNodeColor(element, `profileView.elements[${index}]`);
}

function validateModelViewElement(
  element: ModelViewElement,
  index: number,
  resolver: DefinitionValidationResolver,
) {
  if ("ref" in element) {
    if (element.color !== undefined) {
      toColorNumber(element.color, DEFAULT_MODEL_COLOR);
    }
    resolver.resolveSolid(element.ref, `modelView.elements[${index}].ref`);
    return;
  }

  validateModelNodeColor(element, `modelView.elements[${index}]`);

  if (element.kind === "sweep" && !hasInlineSweepGeometry(element)) {
    resolver.resolveCurve(element.pathRef, `modelView.elements[${index}].pathRef`);
    resolver.resolveCurve(element.profileRef, `modelView.elements[${index}].profileRef`);
  }
}

function validateNormalizedDefinition(definition: OpenElementDefinition): OpenElementDefinition {
  const next = cloneValue(definition);
  const resolver = new DefinitionValidationResolver(next.definitions);

  Object.keys(next.definitions ?? {}).forEach((ref) => {
    resolver.validateDefinition(ref, `definitions.${ref}`);
  });

  next.profileView?.elements.forEach((element, index) => {
    validateProfileViewElement(element, index, resolver);
  });

  next.modelView?.elements.forEach((element, index) => {
    validateModelViewElement(element, index, resolver);
  });

  return next;
}

export function validateOpenElementDefinition(definition: unknown): OpenElementDefinition {
  return validateNormalizedDefinition(normalizeDefinition(definition));
}

export function parseOpenElementDefinition(buffer: string): OpenElementDefinition {
  let parsed: unknown;

  try {
    parsed = JSON.parse(buffer);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Invalid .op buffer: ${message}`);
  }

  if (!isRecord(parsed)) {
    throw new Error("Invalid .op buffer: root value must be an object.");
  }

  return validateOpenElementDefinition(parsed);
}

export function serializeOpenElementDefinition(definition: unknown): string {
  const normalized = typeof definition === "string"
    ? parseOpenElementDefinition(definition)
    : validateOpenElementDefinition(definition);

  return JSON.stringify(toSerializableDefinition(normalized), null, 2);
}

function toSerializableDefinition(config: OpenElementConfig): OpenElementDefinition {
  return {
    version: 1,
    id: config.id,
    labelName: config.labelName,
    type: config.type,
    dimensions: cloneValue(config.dimensions),
    placement: cloneValue(config.placement ?? DEFAULT_PLACEMENT),
    metadata: config.metadata ? cloneValue(config.metadata) : undefined,
    anchors: config.anchors ? cloneValue(config.anchors) : undefined,
    definitions: config.definitions ? cloneValue(config.definitions) : undefined,
    profileView: config.profileView ? cloneValue(config.profileView) : undefined,
    modelView: config.modelView ? cloneValue(config.modelView) : undefined,
  };
}

function toColorNumber(value: ColorValue | undefined, fallback: ColorValue): number {
  const color = value ?? fallback;
  if (typeof color === "number" && Number.isFinite(color)) {
    return color;
  }

  if (typeof color === "string") {
    const trimmed = color.trim();
    const normalized = trimmed.startsWith("#")
      ? trimmed.slice(1)
      : trimmed.toLowerCase().startsWith("0x")
        ? trimmed.slice(2)
        : trimmed;

    const expanded = normalized.length === 3
      ? normalized.split("").map((part) => part + part).join("")
      : normalized;

    if (/^[a-fA-F0-9]{6}$/.test(expanded)) {
      return Number.parseInt(expanded, 16);
    }
  }

  throw new Error(`Unsupported color value "${String(color)}". Use a number or a hex string like "#8B5A2B".`);
}

function createPlacementMatrix(placement?: Placement): THREE.Matrix4 {
  const normalizedPlacement = normalizePlacement(placement);
  const anchor = new THREE.Vector3(...normalizedPlacement.anchor);
  const position = new THREE.Vector3(...normalizedPlacement.position);
  const rotation = new THREE.Euler(
    degToRad(normalizedPlacement.rotation[0]),
    degToRad(normalizedPlacement.rotation[1]),
    degToRad(normalizedPlacement.rotation[2]),
  );
  const quaternion = new THREE.Quaternion().setFromEuler(rotation);
  const scale = new THREE.Vector3(...normalizedPlacement.scale);

  // Compose: M = T(position) * T(anchor) * R * S * T(-anchor).
  // Rotation and scale pivot at `anchor`; the result is then translated by `position`.
  const negAnchor = anchor.clone().multiplyScalar(-1);
  const tNegAnchor = new THREE.Matrix4().makeTranslation(negAnchor.x, negAnchor.y, negAnchor.z);
  const rs = new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0), quaternion, scale);
  const tAnchor = new THREE.Matrix4().makeTranslation(anchor.x, anchor.y, anchor.z);
  const tPosition = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z);

  return tPosition.multiply(tAnchor).multiply(rs).multiply(tNegAnchor);
}

function toPoint2(vector: THREE.Vector3): Point2 {
  return [vector.x, vector.z];
}

function toPoint3(vector: THREE.Vector3): Point3 {
  return [vector.x, vector.y, vector.z];
}

function getTransformedPolylinePoints2D(node: ProfilePolylineNode): Point2[] {
  const matrix = createPlacementMatrix(node.placement);
  return node.points.map(([x, z]) => toPoint2(new THREE.Vector3(x, 0, z).applyMatrix4(matrix)));
}

function getTransformedCurvePoints3D(node: Curve3DNode): Point3[] {
  const matrix = createPlacementMatrix(node.placement);
  return node.points.map((point) => toPoint3(new THREE.Vector3(...point).applyMatrix4(matrix)));
}

function computePointBounds(bounds: ElementBounds, point: THREE.Vector3) {
  bounds.minX = Math.min(bounds.minX, point.x);
  bounds.maxX = Math.max(bounds.maxX, point.x);
  bounds.minZ = Math.min(bounds.minZ, point.z);
  bounds.maxZ = Math.max(bounds.maxZ, point.z);
}

function getFallbackProfileBounds(dimensions: OpenElementDimensions): ElementBounds {
  return {
    minX: -dimensions.width / 2,
    maxX: dimensions.width / 2,
    minZ: -dimensions.depth / 2,
    maxZ: dimensions.depth / 2,
  };
}

function getProfileBounds(profileNodes: ProfileNode[] | undefined, dimensions: OpenElementDimensions): ElementBounds {
  if (!profileNodes || profileNodes.length === 0) {
    return getFallbackProfileBounds(dimensions);
  }

  const bounds: ElementBounds = {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minZ: Number.POSITIVE_INFINITY,
    maxZ: Number.NEGATIVE_INFINITY,
  };

  profileNodes.forEach((node) => {
    const matrix = createPlacementMatrix(node.placement);

    if (node.kind === "polygon") {
      node.vertices.forEach(([x, z]) => computePointBounds(bounds, new THREE.Vector3(x, 0, z).applyMatrix4(matrix)));
      return;
    }

    if (node.kind === "polyline") {
      node.points.forEach(([x, z]) => computePointBounds(bounds, new THREE.Vector3(x, 0, z).applyMatrix4(matrix)));
      return;
    }

    const samples = Math.max(8, node.segments ?? 32);
    for (let index = 0; index <= samples; index += 1) {
      const t = index / samples;
      const angle = node.startAngle + (node.endAngle - node.startAngle) * t;
      const point = new THREE.Vector3(
        node.center[0] + Math.cos(angle) * node.radius,
        0,
        node.center[1] + Math.sin(angle) * node.radius,
      ).applyMatrix4(matrix);
      computePointBounds(bounds, point);
    }
  });

  if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.minZ) || !Number.isFinite(bounds.maxX) || !Number.isFinite(bounds.maxZ)) {
    throw new Error("Unable to determine profile bounds for model-view fallback.");
  }

  return bounds;
}

function applyPlacementToObject(object: THREE.Object3D, placement?: Placement) {
  const matrix = createPlacementMatrix(placement);
  // Use matrixAutoUpdate=false so the composed matrix (which encodes the
  // anchor pivot) is preserved; otherwise THREE recomposes from
  // position/rotation/scale and loses the anchor compensation.
  object.matrix.copy(matrix);
  object.matrixAutoUpdate = false;
  object.updateMatrixWorld(true);

  // Also decompose into position/rotation/scale so consumers reading those
  // fields see something sensible. The decomposition forgets anchor but the
  // matrix is the source of truth.
  const decomposedPosition = new THREE.Vector3();
  const decomposedQuaternion = new THREE.Quaternion();
  const decomposedScale = new THREE.Vector3();
  matrix.decompose(decomposedPosition, decomposedQuaternion, decomposedScale);
  object.position.copy(decomposedPosition);
  object.quaternion.copy(decomposedQuaternion);
  object.scale.copy(decomposedScale);
}

function setOutlineRecursive(object: THREE.Object3D, enabled: boolean) {
  object.traverse((child) => {
    if ("outline" in child) {
      (child as THREE.Object3D & { outline?: boolean }).outline = enabled;
    }
  });
}

function setColorRecursive(object: THREE.Object3D, color: number) {
  object.traverse((child) => {
    if ("color" in child) {
      try {
        (child as THREE.Object3D & { color?: number }).color = color;
      } catch {
        // Fall through to direct material color updates.
      }
    }

    const mesh = child as THREE.Mesh;
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : mesh.material
        ? [mesh.material]
        : [];

    materials.forEach((material) => {
      if ("color" in material && material.color instanceof THREE.Color) {
        material.color.setHex(color);
      }
    });
  });
}

function buildFallbackProfile(dimensions: OpenElementDimensions): ProfilePolygonNode {
  return {
    kind: "polygon",
    id: "fallback-profile",
    color: DEFAULT_PROFILE_COLOR,
    placement: cloneValue(DEFAULT_PLACEMENT),
    vertices: [
      [-dimensions.width / 2, -dimensions.depth / 2],
      [-dimensions.width / 2, dimensions.depth / 2],
      [dimensions.width / 2, dimensions.depth / 2],
      [dimensions.width / 2, -dimensions.depth / 2],
    ],
  };
}

function buildFallbackModel(bounds: ElementBounds, dimensions: OpenElementDimensions): ModelCuboidNode {
  const width = bounds.maxX - bounds.minX;
  const depth = bounds.maxZ - bounds.minZ;
  if (width <= 0 || depth <= 0) {
    throw new Error("Model-view fallback requires positive profile bounds.");
  }

  return {
    kind: "cuboid",
    id: "fallback-model",
    color: DEFAULT_MODEL_COLOR,
    placement: cloneValue(DEFAULT_PLACEMENT),
    center: [(bounds.minX + bounds.maxX) / 2, dimensions.height / 2, (bounds.minZ + bounds.maxZ) / 2],
    width,
    height: dimensions.height,
    depth,
  };
}

function isProfileNode(node: DefinitionNode): node is ProfileNode {
  return node.kind === "polygon" || node.kind === "polyline" || node.kind === "arc";
}

function isCurve3DNode(node: DefinitionNode): node is Curve3DNode {
  return node.kind === "polyline3d";
}

function isModelNode(node: DefinitionNode): node is ModelNode {
  return node.kind === "cuboid" || node.kind === "cylinder" || node.kind === "sweep";
}

function hasInlineSweepGeometry(node: ModelSweepNode): node is ModelSweepDirectNode {
  return "path" in node && "profile" in node;
}

function createProfileObject(node: ProfileNode): THREE.Object3D {
  if (node.kind === "polygon") {
    return new Polygon({
      vertices: node.vertices.map(([x, z]) => new Vector3(x, 0, z)),
      color: toColorNumber(node.color, DEFAULT_PROFILE_COLOR),
    });
  }

  if (node.kind === "polyline") {
    return new Polyline({
      points: node.points.map(([x, z]) => new Vector3(x, 0, z)),
      color: toColorNumber(node.color, DEFAULT_PROFILE_COLOR),
    });
  }

  return new Arc({
    center: new Vector3(node.center[0], 0, node.center[1]),
    radius: node.radius,
    startAngle: node.startAngle,
    endAngle: node.endAngle,
    segments: node.segments ?? 32,
    color: toColorNumber(node.color, DEFAULT_PROFILE_COLOR),
  });
}

function buildModelObject(node: ModelNode, resolver: DefinitionResolver, context: string): BooleanCapableObject {
  if (node.kind === "cuboid") {
    return new Cuboid({
      center: new Vector3(...node.center),
      width: node.width,
      height: node.height,
      depth: node.depth,
      color: toColorNumber(node.color, DEFAULT_MODEL_COLOR),
    }) as BooleanCapableObject;
  }

  if (node.kind === "cylinder") {
    return new Cylinder({
      center: new Vector3(...node.center),
      radius: node.radius,
      height: node.height,
      segments: node.segments ?? 24,
      angle: node.angle ?? Math.PI * 2,
      color: toColorNumber(node.color, DEFAULT_MODEL_COLOR),
    }) as BooleanCapableObject;
  }

  const path = hasInlineSweepGeometry(node)
    ? node.path
    : resolver.resolveCurve(node.pathRef, `${context}.pathRef`).node.points;
  const profile = hasInlineSweepGeometry(node)
    ? node.profile
    : resolver.resolveCurve(node.profileRef, `${context}.profileRef`).node.points;

  const pathPoints = (hasInlineSweepGeometry(node) ? path : getTransformedCurvePoints3D({
    kind: "polyline3d",
    points: path,
    placement: DEFAULT_PLACEMENT,
  })).map((point) => new Vector3(...point));

  const profilePoints = (hasInlineSweepGeometry(node) ? profile : getTransformedCurvePoints3D({
    kind: "polyline3d",
    points: profile,
    placement: DEFAULT_PLACEMENT,
  })).map((point) => new Vector3(...point));

  return new Sweep({
    path: pathPoints,
    profile: profilePoints,
    capStart: node.capStart,
    capEnd: node.capEnd,
    color: toColorNumber(node.color, DEFAULT_MODEL_COLOR),
  }) as BooleanCapableObject;
}

function disposeStandaloneObject(object: THREE.Object3D) {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    if (Array.isArray(mesh.material)) {
      mesh.material.forEach((material) => material.dispose());
    } else if (mesh.material) {
      mesh.material.dispose();
    }
  });

  object.removeFromParent();
}

function executeBooleanChain(
  baseObject: BooleanCapableObject,
  operandObjects: BooleanCapableObject[],
  operation: typeof booleanUnion,
  color: number,
) {
  let current: BooleanCapableObject = baseObject;

  operandObjects.forEach((operand) => {
    const previous = current;
    current = operation(previous, operand, { color }) as BooleanCapableObject;

    if (previous !== baseObject) {
      disposeStandaloneObject(previous);
    }
    disposeStandaloneObject(operand);
  });

  if (current !== baseObject) {
    disposeStandaloneObject(baseObject);
  }

  return current;
}

function executeSubtract(
  baseObject: BooleanCapableObject,
  operandObjects: BooleanCapableObject[],
  color: number,
) {
  if (typeof baseObject.subtract === "function") {
    const result = baseObject.subtract(operandObjects, { color });
    if (result !== baseObject) {
      disposeStandaloneObject(baseObject);
    }
    operandObjects.forEach((operand) => disposeStandaloneObject(operand));
    return result;
  }

  return executeBooleanChain(baseObject, operandObjects, booleanSubtraction, color);
}

function cloneProfileNode(node: ProfileNode): ProfileNode {
  return cloneValue(node);
}

function cloneCurveNode(node: Curve3DNode): Curve3DNode {
  return cloneValue(node);
}

function applyRefOverridesToProfileNode(node: ProfileNode, refNode: RefNode, defaultId: string) {
  const next = cloneProfileNode(node);
  next.id = refNode.id ?? next.id ?? defaultId;
  if (refNode.color !== undefined) {
    next.color = refNode.color;
  }
  if (refNode.placement !== undefined) {
    next.placement = normalizePlacement(refNode.placement);
  }
  return next;
}

class DefinitionResolver {
  private readonly cache = new Map<string, ResolvedDefinitionDescriptor>();
  private readonly stack: string[] = [];

  constructor(private readonly definitions?: OpenElementDefinitions) {}

  resolveProfile(ref: string, context: string): ResolvedProfileDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "profile2d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of profile2d.`);
    }
    return {
      ...resolved,
      node: cloneProfileNode(resolved.node),
    };
  }

  resolveCurve(ref: string, context: string): ResolvedCurveDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "curve3d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of curve3d.`);
    }
    return {
      ...resolved,
      node: cloneCurveNode(resolved.node),
    };
  }

  resolveSolid(ref: string, context: string): ResolvedSolidDescriptor {
    const resolved = this.resolveDefinition(ref, context);
    if (resolved.resultKind !== "solid3d") {
      throw new Error(`${context} references "${ref}", which resolves to ${resolved.resultKind} instead of solid3d.`);
    }
    return resolved;
  }

  private resolveDefinition(ref: string, context: string): ResolvedDefinitionDescriptor {
    if (!this.definitions || !Object.prototype.hasOwnProperty.call(this.definitions, ref)) {
      throw new Error(`${context} references unknown definition "${ref}".`);
    }

    const cached = this.cache.get(ref);
    if (cached) {
      return cached;
    }

    if (this.stack.includes(ref)) {
      throw new Error(`Cyclic generic element definition detected: ${[...this.stack, ref].join(" -> ")}.`);
    }

    this.stack.push(ref);
    try {
      const descriptor = this.buildDescriptor(ref, this.definitions[ref]);
      this.cache.set(ref, descriptor);
      return descriptor;
    } finally {
      this.stack.pop();
    }
  }

  private buildDescriptor(ref: string, node: DefinitionNode): ResolvedDefinitionDescriptor {
    if (isProfileNode(node)) {
      const next = cloneProfileNode(node);
      next.id = next.id ?? ref;
      return {
        resultKind: "profile2d",
        node: next,
        defaultColor: toColorNumber(next.color, DEFAULT_PROFILE_COLOR),
        defaultId: next.id,
      };
    }

    if (isCurve3DNode(node)) {
      const next = cloneCurveNode(node);
      next.id = next.id ?? ref;
      next.points = getTransformedCurvePoints3D(next);
      next.placement = cloneValue(DEFAULT_PLACEMENT);
      return {
        resultKind: "curve3d",
        node: next,
        defaultId: next.id,
      };
    }

    if (isModelNode(node)) {
      const modelNode = cloneValue(node);
      modelNode.id = modelNode.id ?? ref;
      return {
        resultKind: "solid3d",
        defaultColor: toColorNumber(modelNode.color, DEFAULT_MODEL_COLOR),
        defaultId: modelNode.id,
        createObject: () => {
          const object = buildModelObject(modelNode, this, `definitions.${ref}`);
          applyPlacementToObject(object, modelNode.placement);
          return object;
        },
      };
    }

    if (node.kind === "offset2d") {
      const source = this.resolveProfile(node.sourceRef, `definitions.${ref}.sourceRef`);
      if (source.node.kind !== "polyline") {
        throw new Error(`definitions.${ref}.sourceRef must resolve to a polyline for offset2d.`);
      }

      const seedPolyline = new Polyline({
        points: getTransformedPolylinePoints2D(source.node).map(([x, z]) => new Vector3(x, 0, z)),
        color: source.defaultColor,
      });
      const offset = seedPolyline.getOffset(node.distance, node.acuteThresholdDegrees, node.bevel);
      disposeStandaloneObject(seedPolyline);

      const resultNode: ProfileNode = node.output === "polygon"
        ? {
            kind: "polygon",
            id: node.id ?? ref,
            color: node.color ?? source.node.color ?? DEFAULT_PROFILE_COLOR,
            placement: normalizePlacement(node.placement),
            vertices: offset.points.map((point) => [point.x, point.z] satisfies Point2),
          }
        : {
            kind: "polyline",
            id: node.id ?? ref,
            color: node.color ?? source.node.color ?? DEFAULT_PROFILE_COLOR,
            placement: normalizePlacement(node.placement),
            points: offset.points.map((point) => [point.x, point.z] satisfies Point2),
          };

      return {
        resultKind: "profile2d",
        node: resultNode,
        defaultColor: toColorNumber(resultNode.color, DEFAULT_PROFILE_COLOR),
        defaultId: resultNode.id ?? ref,
      };
    }

    const base = this.resolveSolid(node.baseRef, `definitions.${ref}.baseRef`);
    const operands = node.operandRefs.map((operandRef, index) =>
      this.resolveSolid(operandRef, `definitions.${ref}.operandRefs[${index}]`));
    const defaultColor = toColorNumber(node.color, base.defaultColor);

    return {
      resultKind: "solid3d",
      defaultColor,
      defaultId: node.id ?? ref,
      createObject: () => {
        const baseObject = base.createObject();
        const operandObjects = operands.map((operand) => operand.createObject());

        try {
          const result = node.operation === "subtract"
            ? executeSubtract(baseObject, operandObjects, defaultColor)
            : node.operation === "union"
              ? executeBooleanChain(baseObject, operandObjects, booleanUnion, defaultColor)
              : executeBooleanChain(baseObject, operandObjects, booleanIntersection, defaultColor);

          applyPlacementToObject(result, node.placement);
          setColorRecursive(result, defaultColor);
          return result;
        } catch (error) {
          disposeStandaloneObject(baseObject);
          operandObjects.forEach((operand) => disposeStandaloneObject(operand));
          throw error;
        }
      },
    };
  }
}

function resolveProfileElement(
  element: ProfileViewElement,
  index: number,
  resolver: DefinitionResolver,
): ProfileNode {
  if ("ref" in element) {
    return applyRefOverridesToProfileNode(
      resolver.resolveProfile(element.ref, `profileView.elements[${index}].ref`).node,
      element,
      element.ref,
    );
  }

  return cloneProfileNode(element);
}

function buildModelViewObject(
  element: ModelViewElement,
  index: number,
  resolver: DefinitionResolver,
): { key: string; object: BooleanCapableObject; color: number } {
  if ("ref" in element) {
    const descriptor = resolver.resolveSolid(element.ref, `modelView.elements[${index}].ref`);
    const object = descriptor.createObject();
    const color = element.color !== undefined ? toColorNumber(element.color, descriptor.defaultColor) : descriptor.defaultColor;

    if (element.placement !== undefined) {
      applyPlacementToObject(object, element.placement);
    }
    setColorRecursive(object, color);

    return {
      key: element.id ?? descriptor.defaultId,
      object,
      color,
    };
  }

  const object = buildModelObject(element, resolver, `modelView.elements[${index}]`);
  applyPlacementToObject(object, element.placement);
  const color = toColorNumber(element.color, DEFAULT_MODEL_COLOR);
  setColorRecursive(object, color);

  return {
    key: element.id ?? `model-${index}`,
    object,
    color,
  };
}

function findNodeById(
  config: OpenElementConfig,
  nodeId: string,
  section: "profileView" | "modelView" | "definitions",
): Record<string, unknown> | null {
  if (section === "profileView" && config.profileView?.elements) {
    for (const element of config.profileView.elements) {
      if (!("ref" in element) && element.id === nodeId) {
        return element as unknown as Record<string, unknown>;
      }
    }
  } else if (section === "modelView" && config.modelView?.elements) {
    for (const element of config.modelView.elements) {
      if (!("ref" in element) && element.id === nodeId) {
        return element as unknown as Record<string, unknown>;
      }
    }
  } else if (section === "definitions" && config.definitions) {
    for (const [key, node] of Object.entries(config.definitions)) {
      if (node.id === nodeId || key === nodeId) {
        return node as unknown as Record<string, unknown>;
      }
    }
  }
  return null;
}

export class OpenElement extends Polyline implements IShape, PlanVectorExportable {
  ogType: string = ElementType.FURNITURE;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  private isProfileViewEnabled = true;
  private isModelViewEnabled = true;
  private outlineEnabled = true;
  private opBuffer = "";
  private readonly profileEntries: RenderedVisualEntry[] = [];
  private readonly modelEntries: RenderedVisualEntry[] = [];

  propertySet: OpenElementConfig = {
    version: 1,
    id: createOpenPlansId("op-asset"),
    labelName: "Generic Element",
    type: ElementType.FURNITURE,
    dimensions: cloneValue(DEFAULT_DIMENSIONS),
    placement: cloneValue(DEFAULT_PLACEMENT),
    metadata: undefined,
    anchors: undefined,
    definitions: undefined,
    profileView: undefined,
    modelView: undefined,
  };

  get assetId() {
    return this.propertySet.id;
  }

  get outline() {
    return this.outlineEnabled;
  }

  set outline(value: boolean) {
    this.outlineEnabled = value;
    for (const object of this.subElements2D.values()) {
      setOutlineRecursive(object, value);
    }
    for (const object of this.subElements3D.values()) {
      setOutlineRecursive(object, value);
    }
  }

  get labelName() {
    return this.propertySet.labelName;
  }

  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.refreshBuffer();
  }

  get profileView() {
    return this.isProfileViewEnabled;
  }

  set profileView(value: boolean) {
    this.isProfileViewEnabled = value;
    for (const object of this.subElements2D.values()) {
      object.visible = value;
    }
  }

  get modelView() {
    return this.isModelViewEnabled;
  }

  set modelView(value: boolean) {
    this.isModelViewEnabled = value;
    for (const object of this.subElements3D.values()) {
      object.visible = value;
    }
  }

  constructor(config?: OpenElementInput) {
    const resolvedConfig = resolveOpenElementConfig(config);

    super({
      ogid: resolvedConfig.ogid ?? resolvedConfig.id,
      points: [],
      color: 0,
    });

    this.ogid = resolvedConfig.ogid ?? resolvedConfig.id;
    this.userData.ogid = this.ogid;
    this.propertySet = resolvedConfig;
    this.propertySet.ogid = this.ogid;
    this.ogType = this.propertySet.type;
    this.refreshBuffer();
    this.setOPGeometry();
  }

  static parseBuffer(buffer: string): OpenElementDefinition {
    return parseOpenElementDefinition(buffer);
  }

  static normalizeDefinition(definition: unknown): OpenElementDefinition {
    return validateOpenElementDefinition(definition);
  }

  getOPBuffer(): string {
    return this.opBuffer;
  }

  getAnchorDefinition(anchorId: string): AnchorDefinition | undefined {
    const anchor = this.propertySet.anchors?.[anchorId];
    return anchor ? cloneValue(anchor) : undefined;
  }

  getAnchorMatrix(anchorId: string): THREE.Matrix4 {
    const anchor = this.getAnchorDefinition(anchorId);
    if (!anchor) {
      throw new Error(`Unknown anchor "${anchorId}" on asset "${this.propertySet.id}".`);
    }

    return createPlacementMatrix(anchor.placement);
  }

  getExportRoots(view: PlanExportView): THREE.Object3D[] {
    if (view === "top") {
      return Array.from(this.subElements2D.values());
    }

    return Array.from(this.subElements3D.values());
  }

  setOPConfig(config: OpenElementInput): void {
    this.propertySet = resolveOpenElementConfig(config, this.propertySet);
    this.propertySet.ogid = this.ogid;
    this.ogType = this.propertySet.type;
    this.refreshBuffer();
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): OpenElementConfig {
    return cloneValue(this.propertySet);
  }

  getControlSchema(): ControlSchema {
    return buildControlSchema(this.propertySet);
  }

  updateNodeProperty(
    nodeId: string,
    section: "profileView" | "modelView" | "definitions",
    property: string,
    value: number | string,
  ): void {
    const config = cloneValue(this.propertySet);
    const node = findNodeById(config, nodeId, section);
    if (!node) {
      throw new Error(`Node "${nodeId}" not found in ${section}.`);
    }
    (node as Record<string, unknown>)[property] = value;
    this.setOPConfig({ source: config as OpenElementDefinition });
  }

  dispose() {
    this.clearVisualSubElements();
    this.discardGeometry();
  }

  setOPGeometry(): void {
    this.clearVisualSubElements();
    this.setConfig({ points: [], color: 0 });

    const resolver = new DefinitionResolver(this.propertySet.definitions);
    const resolvedProfileNodes = this.propertySet.profileView?.elements?.length
      ? this.propertySet.profileView.elements.map((element, index) => resolveProfileElement(element, index, resolver))
      : [buildFallbackProfile(this.propertySet.dimensions)];
    const profileBounds = getProfileBounds(resolvedProfileNodes, this.propertySet.dimensions);
    const resolvedModelNodes = this.propertySet.modelView?.elements?.length
      ? this.propertySet.modelView.elements.map((element, index) => buildModelViewObject(element, index, resolver))
      : [buildModelViewObject(buildFallbackModel(profileBounds, this.propertySet.dimensions), 0, resolver)];

    resolvedProfileNodes.forEach((node, index) => {
      const object = createProfileObject(node);
      object.name = node.id ?? `profile-${index}`;
      applyPlacementToObject(object, node.placement);
      this.subElements2D.set(object.name, object);
      this.profileEntries.push({
        object,
        color: toColorNumber(node.color, DEFAULT_PROFILE_COLOR),
      });
      this.add(object);
    });

    resolvedModelNodes.forEach((entry, index) => {
      entry.object.name = entry.key || `model-${index}`;
      this.subElements3D.set(entry.object.name, entry.object);
      this.modelEntries.push({
        object: entry.object,
        color: entry.color,
      });
      this.add(entry.object);
    });

    const placement = normalizePlacement(this.propertySet.placement);
    this.position.set(placement.position[0], placement.position[1], placement.position[2]);
    this.rotation.set(degToRad(placement.rotation[0]), degToRad(placement.rotation[1]), degToRad(placement.rotation[2]));
    this.scale.set(placement.scale[0], placement.scale[1], placement.scale[2]);

    this.outline = this.outlineEnabled;
    this.profileView = this.isProfileViewEnabled;
    this.modelView = this.isModelViewEnabled;
  }

  setOPMaterial(): void {
    this.profileEntries.forEach((entry) => {
      setColorRecursive(entry.object, entry.color);
    });
    this.modelEntries.forEach((entry) => {
      setColorRecursive(entry.object, entry.color);
    });
  }

  private clearVisualSubElements() {
    this.profileEntries.length = 0;
    this.modelEntries.length = 0;

    for (const object of this.subElements2D.values()) {
      disposeStandaloneObject(object);
    }
    this.subElements2D.clear();

    for (const object of this.subElements3D.values()) {
      disposeStandaloneObject(object);
    }
    this.subElements3D.clear();
  }

  private refreshBuffer() {
    this.opBuffer = serializeOpenElementDefinition(this.propertySet);
  }
}
